/**
 * SPSA-based CSS-filter solver — ported 1:1 from Priminity's
 * `imagefilter.class.ts`. Given a target RGB color it finds a
 *   `invert(...) sepia(...) saturate(...) hue-rotate(...deg) brightness(...) contrast(...)`
 * chain that, applied to a BLACK source, approximates that color.
 *
 * That lets us tint a single black PNG logo to any accent the theme
 * defines (light or dark, brand-color or platform-color) without
 * shipping an SVG sprite.
 */

type Matrix9 = [number, number, number, number, number, number, number, number, number];

export class ImageFilter {
  r!: number;
  g!: number;
  b!: number;

  constructor(r: number, g: number, b: number) {
    this.set(r, g, b);
  }

  toString(): string {
    return `rgb(${Math.round(this.r)}, ${Math.round(this.g)}, ${Math.round(this.b)})`;
  }

  set(r: number, g: number, b: number): void {
    this.r = this.clamp(r);
    this.g = this.clamp(g);
    this.b = this.clamp(b);
  }

  hueRotate(angle = 0): void {
    const a = (angle / 180) * Math.PI;
    const sin = Math.sin(a);
    const cos = Math.cos(a);
    this.multiply([
      0.213 + cos * 0.787 - sin * 0.213, 0.715 - cos * 0.715 - sin * 0.715, 0.072 - cos * 0.072 + sin * 0.928,
      0.213 - cos * 0.213 + sin * 0.143, 0.715 + cos * 0.285 + sin * 0.140, 0.072 - cos * 0.072 - sin * 0.283,
      0.213 - cos * 0.213 - sin * 0.787, 0.715 - cos * 0.715 + sin * 0.715, 0.072 + cos * 0.928 + sin * 0.072,
    ]);
  }

  grayscale(v = 1): void {
    this.multiply([
      0.2126 + 0.7874 * (1 - v), 0.7152 - 0.7152 * (1 - v), 0.0722 - 0.0722 * (1 - v),
      0.2126 - 0.2126 * (1 - v), 0.7152 + 0.2848 * (1 - v), 0.0722 - 0.0722 * (1 - v),
      0.2126 - 0.2126 * (1 - v), 0.7152 - 0.7152 * (1 - v), 0.0722 + 0.9278 * (1 - v),
    ]);
  }

  sepia(v = 1): void {
    this.multiply([
      0.393 + 0.607 * (1 - v), 0.769 - 0.769 * (1 - v), 0.189 - 0.189 * (1 - v),
      0.349 - 0.349 * (1 - v), 0.686 + 0.314 * (1 - v), 0.168 - 0.168 * (1 - v),
      0.272 - 0.272 * (1 - v), 0.534 - 0.534 * (1 - v), 0.131 + 0.869 * (1 - v),
    ]);
  }

  saturate(v = 1): void {
    this.multiply([
      0.213 + 0.787 * v, 0.715 - 0.715 * v, 0.072 - 0.072 * v,
      0.213 - 0.213 * v, 0.715 + 0.285 * v, 0.072 - 0.072 * v,
      0.213 - 0.213 * v, 0.715 - 0.715 * v, 0.072 + 0.928 * v,
    ]);
  }

  multiply(m: Matrix9): void {
    const nR = this.clamp(this.r * m[0] + this.g * m[1] + this.b * m[2]);
    const nG = this.clamp(this.r * m[3] + this.g * m[4] + this.b * m[5]);
    const nB = this.clamp(this.r * m[6] + this.g * m[7] + this.b * m[8]);
    this.r = nR; this.g = nG; this.b = nB;
  }

  brightness(v = 1): void { this.linear(v); }
  contrast(v = 1): void { this.linear(v, -(0.5 * v) + 0.5); }

  linear(slope = 1, intercept = 0): void {
    this.r = this.clamp(this.r * slope + intercept * 255);
    this.g = this.clamp(this.g * slope + intercept * 255);
    this.b = this.clamp(this.b * slope + intercept * 255);
  }

  invert(v = 1): void {
    this.r = this.clamp((v + (this.r / 255) * (1 - 2 * v)) * 255);
    this.g = this.clamp((v + (this.g / 255) * (1 - 2 * v)) * 255);
    this.b = this.clamp((v + (this.b / 255) * (1 - 2 * v)) * 255);
  }

  hsl(): { h: number; s: number; l: number } {
    const r = this.r / 255;
    const g = this.g / 255;
    const b = this.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return { h: h * 100, s: s * 100, l: l * 100 };
  }

  private clamp(v: number): number {
    if (v > 255) return 255;
    if (v < 0) return 0;
    return v;
  }
}

interface SolveResult {
  values: number[];
  loss: number;
  filter: string;
}

export class Solver {
  private target: ImageFilter;
  private targetHSL: { h: number; s: number; l: number };
  private reused: ImageFilter;

  constructor(target: ImageFilter) {
    this.target = target;
    this.targetHSL = target.hsl();
    this.reused = new ImageFilter(0, 0, 0);
  }

  solve(): SolveResult {
    const wide = this.solveWide();
    const result = this.solveNarrow(wide);
    return {
      values: result.values,
      loss: result.loss,
      filter: this.css(result.values),
    };
  }

  private solveWide(): { values: number[]; loss: number } {
    const A = 5;
    const c = 15;
    const a = [60, 180, 18000, 600, 1.2, 1.2];

    let best: { values: number[]; loss: number } = { values: [], loss: Infinity };
    for (let i = 0; best.loss > 25 && i < 3; i++) {
      const initial = [50, 20, 3750, 50, 100, 100];
      const result = this.spsa(A, a, c, initial, 1000);
      if (result.loss < best.loss) best = result;
    }
    return best;
  }

  private solveNarrow(wide: { values: number[]; loss: number }): {
    values: number[];
    loss: number;
  } {
    const A = wide.loss;
    const c = 2;
    const A1 = A + 1;
    const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
    return this.spsa(A, a, c, wide.values, 500);
  }

  private spsa(
    A: number,
    a: number[],
    c: number,
    values: number[],
    iters: number,
  ): { values: number[]; loss: number } {
    const alpha = 1;
    const gamma = 1 / 6;

    let best: number[] = values.slice(0);
    let bestLoss = Infinity;
    const deltas = new Array(6);
    const highArgs = new Array(6);
    const lowArgs = new Array(6);

    for (let k = 0; k < iters; k++) {
      const ck = c / Math.pow(k + 1, gamma);
      for (let i = 0; i < 6; i++) {
        deltas[i] = Math.random() > 0.5 ? 1 : -1;
        highArgs[i] = values[i] + ck * deltas[i];
        lowArgs[i] = values[i] - ck * deltas[i];
      }
      const lossDiff = this.loss(highArgs) - this.loss(lowArgs);
      for (let i = 0; i < 6; i++) {
        const g = ((lossDiff / (2 * ck)) * deltas[i]);
        const ak = a[i] / Math.pow(A + k + 1, alpha);
        values[i] = fix(values[i] - ak * g, i);
      }
      const loss = this.loss(values);
      if (loss < bestLoss) {
        best = values.slice(0);
        bestLoss = loss;
      }
    }
    return { values: best, loss: bestLoss };

    function fix(value: number, idx: number): number {
      let max = 100;
      if (idx === 2) max = 7500; // saturate
      else if (idx === 4 || idx === 5) max = 200; // brightness / contrast
      if (idx === 3) {
        // hue-rotate
        if (value > max) value = value % max;
        else if (value < 0) value = max + (value % max);
      } else if (value < 0) value = 0;
      else if (value > max) value = max;
      return value;
    }
  }

  private loss(filters: number[]): number {
    const color = this.reused;
    color.set(0, 0, 0);
    color.invert(filters[0] / 100);
    color.sepia(filters[1] / 100);
    color.saturate(filters[2] / 100);
    color.hueRotate(filters[3] * 3.6);
    color.brightness(filters[4] / 100);
    color.contrast(filters[5] / 100);
    const hsl = color.hsl();
    return (
      Math.abs(color.r - this.target.r) +
      Math.abs(color.g - this.target.g) +
      Math.abs(color.b - this.target.b) +
      Math.abs(hsl.h - this.targetHSL.h) +
      Math.abs(hsl.s - this.targetHSL.s) +
      Math.abs(hsl.l - this.targetHSL.l)
    );
  }

  private css(filters: number[]): string {
    const fmt = (i: number, mul = 1): number => Math.round(filters[i] * mul);
    return (
      `invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) ` +
      `hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%)`
    );
  }
}

/**
 * Solve a CSS `filter:` value that turns BLACK into the given RGB color.
 * Retries up to a few times until loss < 10 (Priminity uses the same
 * heuristic — small loss means the color is visually accurate).
 */
export function solveFilterForRgb(r: number, g: number, b: number, maxAttempts = 5): string {
  for (let i = 0; i < maxAttempts; i++) {
    const solver = new Solver(new ImageFilter(r, g, b));
    const result = solver.solve();
    if (result.loss < 10) return result.filter;
  }
  // Fall back to the last attempt regardless of loss — visually still close.
  return new Solver(new ImageFilter(r, g, b)).solve().filter;
}

/** Parse `#rgb`, `#rrggbb`, or `rgb(r,g,b)` to a tuple. */
export function parseRgb(input: string): [number, number, number] | null {
  const s = input.trim();
  const hexMatch = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const rgbMatch = s.match(/rgba?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }
  return null;
}
